// SEAT-DRAWER-CLICKS (Andrei, 2026-10-28: clicking things in the seat drawer still went to a
// page): an item row in the drawer opens that item in place, over the chart, and the drawer stays
// open on its seat. Each modal is built from the editors the app already has:
//   - a measurable: this week's actual against its target and its reporting state; a manager
//     types this week's value (the Performance modal's entry row) and edits the KPI (the seat
//     page's KPI dialog); the seat's self-reporting holder goes on to report its numbers; the
//     scorecard stays one marked link away;
//   - a responsibility: the seat page's responsibility dialog for a manager, its details read
//     only for anyone else.
// A priority opens `PriorityDialog` (a writer) or `PriorityDetailModal` (a reader), and a task
// Work's task drawer (`TasksModal`). Every save refreshes the chart's reads, the drawer's too.
// SEAT-DRAWER-UX §2.1: the measurable's modal is the KPI modal (`KpiDetailModal`): this week,
// the 13-week trend, this week's entry, the settings (target, scoring, reporting), the history,
// and Edit / Remove / the scorecard link. `GoalPicker` weights an existing KPI as a goal.
import { ApiError } from "@companyos/sdk";
import {
  Badge,
  Button,
  cn,
  LensBandDot,
  NativeSelect,
  NativeSelectOption,
  Sparkline,
} from "@companyos/ui";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { PencilIcon, Trash2Icon } from "lucide-react";
import { useEffect, useId, useState } from "react";
import type { Api } from "../../api/client";
import type { ScorecardMetric, SeatMetric, TargetRule } from "../../api/types";
import { shiftWeek, weekRangeLabel } from "../../focus/weeks";

/** The KPI modal's history window: the trend's 13 weeks. */
const HISTORY_WEEKS = 13;

import type { CompanyAccess } from "../company";
import { historyRows } from "../company/scorecard-model";
import { formatCardMetric, formatDateTime, formatMetricValue } from "../format";
import { HEALTH_LABEL, HEALTH_TEXT, lineHealth } from "../kpi/health";
import { KpiAssignDialog } from "../kpi/kpi-assign-dialog";
import { seatSetQuery, seatTargetsQuery } from "../kpi/queries";
import { holderText, type SeatHolder } from "../kpi/seat-kpis";
import { companyQuery, responsibilitiesQuery, seatScorecardQuery } from "../queries";
import { QuickAddPreparing } from "../quick-add/preparing";
import { ReportingStatusChip, reportingProps } from "../reporting/reporting-status";
import { Loading } from "../screen-parts";
import { ErrorNotice } from "../structure/parts";
import { ResponsibilityDialog } from "../structure/responsibilities";
import { ScorecardLink, useSeatMeasurables, WeekEntry } from "./seat-section-modals";
import { PageArrow, SectionModal } from "./section-modal";

type SeatRef = { id: string; title: string; status: string };

const DIRECTION_TEXT: Record<ScorecardMetric["direction"], string> = {
  gt: "higher is better",
  gte: "higher is better",
  lt: "lower is better",
  lte: "lower is better",
  between: "in range",
  eq: "exactly on target",
};

/** A drawer KPI row's modal (Performance, Execution, Record). */
export function KpiDetailModal({
  api,
  company,
  seat,
  holders,
  week,
  metricId,
  name,
  canEdit,
  startEditing = false,
  focusTarget = false,
  names,
  onRemove,
  onReport,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  seat: SeatRef;
  holders: readonly SeatHolder[];
  week: string;
  metricId: string;
  /** The row's name, shown while the week's scorecard loads. */
  name: string;
  /** A manager with the KPI add-on writable, on an open seat: types the value, edits the KPI. */
  canEdit: boolean;
  /** Open the KPI editor as soon as it can (a row's hover ✎). */
  startEditing?: boolean;
  /** Execution: the Target line is the one the row is about. */
  focusTarget?: boolean;
  /** Membership id → display name, for the history's "who". */
  names: ReadonlyMap<string, string>;
  /** Remove from the seat (the panel's confirm); a manager's. */
  onRemove?: (() => void) | undefined;
  /** The seat's self-reporting holder: on to the Performance modal's own numbers. */
  onReport?: (() => void) | undefined;
  onClose: () => void;
}) {
  const co = company.id;
  const id = useId();
  const card = useQuery(seatScorecardQuery(api, co, seat.id, week));
  const set = useQuery({ ...seatSetQuery(api, co, seat.id), enabled: canEdit });
  const targets = useQuery({ ...seatTargetsQuery(api, co, seat.id), enabled: canEdit });
  const detail = useQuery({ ...companyQuery(api, co), enabled: canEdit });
  // The history: every weekly value on the seat in the trend's 13 weeks, superseded ones too
  // (a manager's read). The list is oldest first and paged, so every page is read.
  const history = useQuery({
    queryKey: ["company", co, "observations", "kpi-modal", metricId, seat.id, week],
    queryFn: async () => {
      const data: Awaited<ReturnType<typeof api.client.observations.list>>["data"] = [];
      let cursor: string | undefined;
      for (let i = 0; i < 10; i++) {
        const p = await api.client.observations.list({
          params: { co },
          query: {
            metric: metricId,
            subject_kind: "seat",
            subject_id: seat.id,
            period_type: "week",
            from: shiftWeek(week, -(HISTORY_WEEKS - 1)),
            to: week,
            limit: 200,
            ...(cursor ? { cursor } : {}),
          },
        });
        data.push(...p.data);
        if (!p.next_cursor) break;
        cursor = p.next_cursor;
      }
      return { data };
    },
    enabled: canEdit,
    retry: false,
  });
  const [editing, setEditing] = useState<{ row: SeatMetric; target: TargetRule | null } | null>(
    null,
  );
  const m = card.data?.metrics.find((x) => x.metric_def_id === metricId);
  const row = set.data?.data.find((r) => r.metric_def_id === metricId);
  // The KPI editor opens with the seat's stored target (never a null read while it loads).
  const editable = canEdit && row !== undefined && targets.isSuccess ? row : null;
  const rule =
    (targets.data?.data ?? [])
      .filter((t) => t.metric_def_id === metricId && t.effective_to === null)
      .sort((a, b) => b.effective_from.localeCompare(a.effective_from))[0] ?? null;
  // A row's ✎: the editor opens once its reads are in (once).
  const [autoOpened, setAutoOpened] = useState(false);
  useEffect(() => {
    if (startEditing && !autoOpened && editable) {
      setAutoOpened(true);
      setEditing({ row: editable, target: rule });
    }
  }, [startEditing, autoOpened, editable, rule]);
  const h = m ? (lineHealth(m) ?? "none") : "none";
  const trendPoints = m ? m.trend.filter((p) => p.actual !== null).length : 0;
  return (
    <SectionModal
      title={m?.name ?? name}
      info="A KPI on the seat's scorecard: this week's value against its target, its trend and how it is scored."
      description={`${seat.title} · ${weekRangeLabel(week)}${m ? ` · ${DIRECTION_TEXT[m.direction]}` : ""}`}
      onClose={onClose}
      slot="measurable-modal"
      size={canEdit ? "lg" : "default"}
      footer={
        <>
          {onRemove ? (
            <Button
              type="button"
              variant="ghost"
              className="mr-auto text-danger-text"
              onClick={onRemove}
              data-slot="remove-kpi"
            >
              <Trash2Icon data-icon="inline-start" aria-hidden="true" />
              Remove from seat
            </Button>
          ) : null}
          <ScorecardLink
            company={company}
            seatId={seat.id}
            kpi={metricId}
            className="self-center"
          />
          {editable ? (
            <Button
              type="button"
              variant="outline"
              onClick={() => setEditing({ row: editable, target: rule })}
              data-slot="edit-kpi"
            >
              <PencilIcon data-icon="inline-start" aria-hidden="true" />
              Edit KPI
            </Button>
          ) : null}
          <Button type="button" variant="secondary" onClick={onClose}>
            Done
          </Button>
        </>
      }
    >
      {card.isError ? (
        <ErrorNotice what="This week's scorecard" error={card.error} />
      ) : card.isPending ? (
        <Loading label="Loading this week's value" rows={1} />
      ) : !m ? (
        <p className="text-xs text-faint">Not on this week's scorecard.</p>
      ) : (
        <div className="flex flex-col gap-3" data-slot="measurable-detail">
          <dl className="grid grid-cols-4 gap-2 text-xs">
            <div>
              <dt className="text-faint">This week</dt>
              <dd className={cn("font-medium tabular-nums", HEALTH_TEXT[h])}>
                {formatCardMetric(m.actual, m.unit)}
              </dd>
            </div>
            <div data-highlight={focusTarget ? "" : undefined}>
              <dt className="text-faint">Target</dt>
              <dd className="font-medium tabular-nums">
                {m.target === null ? "No target" : formatCardMetric(m.target, m.unit)}
              </dd>
            </div>
            <div>
              <dt className="text-faint">Attainment</dt>
              <dd className="font-medium tabular-nums">
                {m.attainment === null ? "—" : `${Math.round(m.attainment * 100)}%`}
              </dd>
            </div>
            <div>
              <dt className="text-faint">Health</dt>
              <dd className="flex items-center gap-1 font-medium">
                <LensBandDot band={h} />
                {HEALTH_LABEL[h]}
              </dd>
            </div>
          </dl>
          <section className="flex flex-col gap-1" aria-labelledby={`${id}-trend`}>
            <h3 id={`${id}-trend`} className="text-xs font-medium text-muted-foreground">
              Trend · {m.trend.length} weeks
            </h3>
            {trendPoints >= 2 ? (
              <div className="flex items-center gap-3" data-slot="kpi-trend">
                <Sparkline
                  values={m.trend.map((p) => p.actual)}
                  reference={m.trend.map((p) => p.target)}
                  label={`${m.name}: ${m.trend
                    .map((p) => `${p.period_key} ${p.actual === null ? "no data" : p.actual}`)
                    .join(", ")}`}
                  className="h-10 w-48"
                />
                <span className="text-xs text-faint">
                  {m.trend[0]?.period_key} – {m.trend[m.trend.length - 1]?.period_key} ·{" "}
                  {trendPoints} of {m.trend.length} weeks
                </span>
              </div>
            ) : (
              <p className="text-xs text-faint">Not enough weeks yet for a trend.</p>
            )}
          </section>
        </div>
      )}
      {canEdit ? (
        <WeekEntry
          api={api}
          company={company}
          seatId={seat.id}
          week={week}
          manager
          only={metricId}
        />
      ) : null}
      {canEdit && (set.isError || targets.isError) ? (
        <ErrorNotice what="The KPI's settings" error={set.error ?? targets.error} />
      ) : null}
      {m ? (
        <section className="flex flex-col gap-1.5" aria-labelledby={`${id}-settings`}>
          <h3 id={`${id}-settings`} className="text-xs font-medium text-muted-foreground">
            Settings
          </h3>
          <dl
            className="grid grid-cols-[6rem_1fr] gap-x-3 gap-y-1 text-xs"
            data-slot="kpi-settings"
          >
            <dt className="text-faint">Target</dt>
            <dd
              className={cn("text-secondary", focusTarget && "font-medium text-foreground")}
              data-highlight={focusTarget ? "" : undefined}
            >
              {rule
                ? `${formatCardMetric(Number(rule.value), m.unit)} from ${rule.effective_from} (seat rule)`
                : m.target !== null
                  ? `${formatCardMetric(m.target, m.unit)} (company rule)`
                  : "No target"}
            </dd>
            <dt className="text-faint">Scoring</dt>
            <dd className="text-secondary">
              ×kpi {m.kpi_weight} · ×goal {m.goal_weight} · {m.tracking_mode}
            </dd>
            <dt className="text-faint">Reporting</dt>
            <dd>
              <ReportingStatusChip pageMark={<PageArrow />} {...reportingProps(m)} />
            </dd>
          </dl>
        </section>
      ) : null}
      {canEdit ? (
        <section className="flex flex-col gap-1.5" aria-labelledby={`${id}-history`}>
          <h3 id={`${id}-history`} className="text-xs font-medium text-muted-foreground">
            History
          </h3>
          {history.error instanceof ApiError && history.error.status === 403 ? (
            <p className="text-xs text-faint">History is visible to managers.</p>
          ) : history.isError ? (
            <ErrorNotice what="The history" error={history.error} />
          ) : history.isPending ? (
            <Loading label="Loading the history" rows={2} />
          ) : (
            <KpiHistory
              rows={historyRows(history.data.data, names)}
              unit={m?.unit ?? "number"}
              currency={detail.data?.reporting_currency ?? "USD"}
            />
          )}
        </section>
      ) : null}
      {onReport ? (
        <Button
          variant="outline"
          size="sm"
          className="self-start"
          onClick={onReport}
          data-slot="report-numbers"
        >
          Report my numbers
        </Button>
      ) : null}
      {editing && set.data ? (
        <KpiAssignDialog
          api={api}
          company={company}
          seat={{
            seatId: seat.id,
            seatTitle: seat.title,
            holderText: holderText(holders),
            agent: holders.some((h) => h.kind === "digital_employee"),
          }}
          onSeat={set.data.data}
          editing={editing}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </SectionModal>
  );
}

/** The KPI's last values on the seat, newest first: the value, who and how, the note. */
function KpiHistory({
  rows,
  unit,
  currency,
}: {
  rows: ReturnType<typeof historyRows>;
  unit: ScorecardMetric["unit"];
  currency: string;
}) {
  if (!rows.length) return <p className="text-xs text-faint">No value recorded yet.</p>;
  return (
    <ol
      className="flex max-h-64 flex-col divide-y divide-subtle overflow-y-auto text-xs"
      data-slot="kpi-history"
    >
      {rows.map((r) => (
        <li key={r.id} data-state={r.state} className="flex flex-col gap-0.5 py-1.5">
          <span className="flex items-center gap-2">
            <span
              className={cn(
                "font-medium tabular-nums",
                r.state === "live" ? "text-foreground" : "text-faint line-through",
              )}
            >
              {r.value === null ? "—" : formatMetricValue(r.value, unit, currency)}
            </span>
            <span className="text-faint">
              {r.who} · {r.source}
            </span>
            <span className="ml-auto text-faint">{formatDateTime(r.at)}</span>
          </span>
          {r.note ? <span className="text-secondary">“{r.note}”</span> : null}
        </li>
      ))}
    </ol>
  );
}

/**
 * SEAT-DRAWER-UX §2.2: Execution's "Weight an existing KPI": pick one of the seat's scored KPIs
 * with no goal weight, then its editor opens on it (set the goal weight there).
 */
export function GoalPicker({
  api,
  company,
  seat,
  holders,
  choices,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  seat: SeatRef;
  holders: readonly SeatHolder[];
  choices: readonly Pick<ScorecardMetric, "metric_def_id" | "name">[];
  onClose: () => void;
}) {
  const id = useId();
  const co = company.id;
  const set = useQuery(seatSetQuery(api, co, seat.id));
  const targets = useQuery(seatTargetsQuery(api, co, seat.id));
  const [picked, setPicked] = useState(choices[0]?.metric_def_id ?? "");
  const [editing, setEditing] = useState<{ row: SeatMetric; target: TargetRule | null } | null>(
    null,
  );
  const row = set.data?.data.find((r) => r.metric_def_id === picked);
  const ready = row !== undefined && targets.isSuccess;
  if (editing && set.data)
    return (
      <KpiAssignDialog
        api={api}
        company={company}
        seat={{
          seatId: seat.id,
          seatTitle: seat.title,
          holderText: holderText(holders),
          agent: holders.some((h) => h.kind === "digital_employee"),
        }}
        onSeat={set.data.data}
        editing={editing}
        onClose={onClose}
      />
    );
  return (
    <SectionModal
      title="Weight a KPI as a goal"
      info="A goal is a KPI with a goal weight: it counts toward how far the seat is (attainment), not only hit or miss."
      description={seat.title}
      onClose={onClose}
      slot="goal-picker"
      size="default"
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={!ready}
            onClick={() => {
              if (!row) return;
              const rule =
                (targets.data?.data ?? [])
                  .filter((t) => t.metric_def_id === picked && t.effective_to === null)
                  .sort((a, b) => b.effective_from.localeCompare(a.effective_from))[0] ?? null;
              setEditing({ row, target: rule });
            }}
          >
            Next
          </Button>
        </>
      }
    >
      {set.isError || targets.isError ? (
        <ErrorNotice what="The seat's KPIs" error={set.error ?? targets.error} />
      ) : null}
      <label htmlFor={`${id}-kpi`} className="text-sm text-secondary">
        KPI
      </label>
      <NativeSelect id={`${id}-kpi`} value={picked} onChange={(e) => setPicked(e.target.value)}>
        {choices.map((c) => (
          <NativeSelectOption key={c.metric_def_id} value={c.metric_def_id}>
            {c.name}
          </NativeSelectOption>
        ))}
      </NativeSelect>
      <p className="text-xs text-faint">
        Next opens its editor: set a <Badge variant="neutral">Goal weight</Badge> above 0.
      </p>
    </SectionModal>
  );
}

/** A drawer responsibility row's modal: its editor for a manager, its details otherwise. */
export function ResponsibilityItemModal({
  api,
  company,
  seat,
  responsibilityId,
  summary,
  canEdit,
  kpiOn,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  seat: SeatRef;
  responsibilityId: string;
  /** What the drawer row knows (the chart's dimensions): a reader's details, with no read of the
   *  seat's list (which only a manager may read). */
  summary: ResponsibilitySummary;
  canEdit: boolean;
  /** The KPI add-on is on: the editor offers the seat's measurables to link. */
  kpiOn: boolean;
  onClose: () => void;
}) {
  const q = useInfiniteQuery({
    ...responsibilitiesQuery(api, company.id, seat.id),
    enabled: canEdit,
  });
  const measurables = useSeatMeasurables(api, company.id, seat.id, kpiOn && canEdit);
  const r = q.data?.pages.flatMap((p) => p.data).find((x) => x.id === responsibilityId);
  // A seat with more responsibilities than a page: read on until the one clicked is there.
  const more = canEdit && !r && q.hasNextPage && !q.isFetchingNextPage;
  const { fetchNextPage } = q;
  useEffect(() => {
    if (more) void fetchNextPage();
  }, [more, fetchNextPage]);
  if (!canEdit)
    return (
      <SectionModal
        title={summary.name}
        description={`${seat.title} · Responsibility`}
        onClose={onClose}
        slot="responsibility-detail"
        size="default"
      >
        <dl className="grid grid-cols-2 gap-2 text-xs">
          <div>
            <dt className="text-faint">Weight</dt>
            <dd className="font-medium tabular-nums">×{Number(summary.weight)}</dd>
          </div>
          <div>
            <dt className="text-faint">Measured</dt>
            <dd className="font-medium">
              {summary.measured ? "Yes, by a measurable" : "No measurable"}
            </dd>
          </div>
        </dl>
        {summary.description ? (
          <p className="text-sm break-words whitespace-pre-line text-secondary">
            {summary.description}
          </p>
        ) : null}
      </SectionModal>
    );
  if (!r) {
    if (q.isPending || q.isError || q.hasNextPage)
      return (
        <QuickAddPreparing
          title="Responsibility"
          failed={q.isError}
          retry={() => void q.refetch()}
          onClose={onClose}
        />
      );
    return (
      <SectionModal title="Responsibility" onClose={onClose} slot="responsibility-detail">
        <p className="text-xs text-faint">This responsibility is no longer on the seat.</p>
      </SectionModal>
    );
  }
  return (
    <ResponsibilityDialog
      api={api}
      co={company.id}
      seatId={seat.id}
      seatTitle={seat.title}
      current={r}
      measurables={measurables}
      onClose={onClose}
    />
  );
}

/** A responsibility as the drawer row shows it. */
export type ResponsibilitySummary = {
  name: string;
  weight: string | number;
  measured: boolean;
  description?: string | null;
};
